import { ConnectorCardReducerState } from '@/types/reducers';
import { Badge, Flex, Text } from '@tremor/react';
import { Activity, CheckCircle, Settings, XCircle } from 'lucide-react';

interface Props {
  state: ConnectorCardReducerState;
}

const getStatusColor = (state: ConnectorCardReducerState) => {
  switch (state.status) {
    case 'Failure':
      return 'text-red-600 bg-red-50 border-red-200';
    case 'Syncing':
      return 'text-blue-600 bg-blue-50 border-blue-200';
    case 'Under_Setup':
      return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    case 'Activating':
    case 'Deactivating':
    case 'Deleting':
      return 'bg-primary-50 text-primary-700 border-primary-200';

    default:
      if (state.isActive) {
        return 'text-green-600 bg-green-50 border-green-200';
      }
      return 'text-gray-600 bg-gray-50 border-gray-200';
  }
};

const getStatusIcon = (state: ConnectorCardReducerState) => {
  switch (state.status) {
    case 'Failure':
      return XCircle;
    case 'Syncing':
      return Activity;
    case 'Under_Setup':
      return Settings;
    case 'Activating':
    case 'Deactivating':
    case 'Deleting':
      return function BadgeSpinner() {
        return (
          <div className="w-3 h-3 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
        );
      };
    default:
      if (state.isActive) {
        return CheckCircle;
      }
      return XCircle;
  }
};

const getStatusText = (state: ConnectorCardReducerState) => {
  switch (state.status) {
    case 'Failure':
      return 'Error';
    case 'Syncing':
      return 'Syncing';
    case 'Under_Setup':
      return 'Under Setup';
    case 'Activating':
      return 'Activating';
    case 'Deactivating':
      return 'Deactivating';
    case 'Deleting':
      return 'Deleting';
    default:
      return state.isActive ? 'Active' : 'Inactive';
  }
};

const statusToSpinIcon: ConnectorCardReducerState['status'][] = [
  'Syncing',
  'Under_Setup',
  'Activating',
  'Deactivating',
  'Deleting',
];

export function ConnectorCardBadge({ state }: Props) {
  const statusColor = getStatusColor(state);
  const StatusIcon = getStatusIcon(state);
  const statusText = getStatusText(state);

  return (
    <Badge
      className={[
        statusColor,
        'px-2 py-1 rounded-full border text-xs font-medium ml-auto',
      ].join(' ')}
    >
      <Flex className="gap-1">
        <StatusIcon
          className={[
            'w-3 h-3',
            statusToSpinIcon.find((status) => status === state.status)
              ? 'animate-spin'
              : '',
          ].join(' ')}
        />
        <Text className="text-inherit capitalize">{statusText}</Text>
      </Flex>
    </Badge>
  );
}
